import { Core } from '@walletconnect/core'
import { WalletKit, WalletKitTypes, type IWalletKit } from '@reown/walletkit'
import { buildApprovedNamespaces, getSdkError } from '@walletconnect/utils'
import { APP_METADATA, CHAIN_ID, MAIN_SAFE, WC_PROJECT_ID } from './constants.js'

/**
 * WalletKit (Side A): SafeWard is a *wallet* that dApps connect to. The
 * advertised account is the Main Safe — dApps see Main Safe's balances /
 * positions. When a dApp sends `eth_sendTransaction` or `wallet_sendCalls`,
 * the App handler wraps it as a Module Safe tx, asks Rabby for Ainura's
 * sig (Side B), and proposes to Safe Tx Service.
 *
 * Namespace approval is modeled after Safe{Wallet}'s
 * `WalletConnectWallet.ts:getNamespaces` — we approve every chain the dApp
 * asks for, with the same Safe address on each (Safe deployments are
 * deterministic, but even when the Safe doesn't exist on a chain we lie
 * because dApps like Jumper/LiFi require multi-chain support up-front in
 * the proposal even if they only send tx requests on the source chain).
 */

/**
 * Mirrors Safe{Wallet}'s `SAFE_COMPATIBLE_METHODS`. Includes all the eth_*
 * read methods that viem-based dApps fire as part of preflight (gas /
 * balance / nonce checks). If we don't advertise them, namespace
 * validation rejects them at the relay before our wallet ever sees them
 * and the dApp surfaces "Unknown RPC error" with no detail.
 */
const SUPPORTED_METHODS = [
  'eth_accounts',
  'net_version',
  'eth_chainId',
  'personal_sign',
  'eth_sign',
  'eth_signTypedData',
  'eth_signTypedData_v4',
  'eth_signTransaction',
  'eth_sendTransaction',
  'eth_blockNumber',
  'eth_getBalance',
  'eth_getCode',
  'eth_getTransactionCount',
  'eth_getStorageAt',
  'eth_getBlockByNumber',
  'eth_getBlockByHash',
  'eth_getTransactionByHash',
  'eth_getTransactionReceipt',
  'eth_estimateGas',
  'eth_call',
  'eth_getLogs',
  'eth_gasPrice',
  'eth_maxPriorityFeePerGas',
  'eth_feeHistory',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
  'wallet_sendCalls',
  'wallet_getCapabilities',
  'wallet_getCallsStatus',
  'wallet_showCallsStatus',
] as const

const SUPPORTED_EVENTS = ['chainChanged', 'accountsChanged']

const CHAIN_HEX = `0x${CHAIN_ID.toString(16)}`

/**
 * Mirrors Safe{Wallet}'s sessionProperties exactly:
 *   {
 *     atomic: '{"status":"supported"}',
 *     capabilities: '{<safeAddress>: {<chainHex>: {atomicBatch: {supported: true}}}}'
 *   }
 *
 * They send BOTH the modern top-level `atomic` blob and the legacy per-chain
 * `atomicBatch.supported` shape. Different dApp versions check different
 * shapes; covering both keeps maximum compatibility.
 */
function buildSessionProperties(chainHexes: string[]) {
  const perChain: Record<string, { atomicBatch: { supported: true } }> = {}
  for (const hex of chainHexes) {
    perChain[hex] = { atomicBatch: { supported: true as const } }
  }
  return {
    atomic: JSON.stringify({ status: 'supported' }),
    capabilities: JSON.stringify({ [MAIN_SAFE]: perChain }),
  }
}

let kit: IWalletKit | null = null
let initPromise: Promise<IWalletKit> | null = null

export async function getWalletKit(): Promise<IWalletKit> {
  if (kit) return kit
  if (initPromise) return initPromise

  initPromise = (async () => {
    // CRITICAL: customStoragePrefix isolates WalletKit's session +
    // keychain storage from AppKit's. We have BOTH WC clients in this
    // tab — AppKit (Rabby connection, Side B) and WalletKit (dApp
    // connections, Side A). Without this prefix they both write to
    // `wc@2:*` localStorage keys and stomp on each other's keychains,
    // producing the "No matching key" / "An unknown RPC error" failures.
    // Same pattern Safe{Wallet} uses: see
    // safe-wallet-monorepo/apps/web/src/features/walletconnect/services/WalletConnectWallet.ts
    const core = new Core({
      projectId: WC_PROJECT_ID,
      customStoragePrefix: 'safeward',
      logger: 'debug',
    })
    const instance = await WalletKit.init({
      core,
      metadata: APP_METADATA,
    })

    instance.on('session_delete', (e) => {
      console.warn('[safeward] session_delete', e)
    })
    instance.on('proposal_expire', (e) => {
      console.warn('[safeward] proposal_expire', e)
    })
    instance.on('session_request_expire', (e) => {
      console.warn('[safeward] session_request_expire', e)
    })

    kit = instance
    return instance
  })()

  // Don't cache a rejected init forever — a transient relay outage or
  // IndexedDB hiccup at startup would otherwise wedge every later
  // pair / disconnect / respond call until full reload.
  initPromise.catch(() => {
    initPromise = null
  })

  return initPromise
}

const EIP155 = 'eip155' as const

function stripEip155Prefix(eip155ChainId: string): string {
  return eip155ChainId.split(':').pop() ?? ''
}

function getEip155ChainId(chainId: string | number): string {
  return `${EIP155}:${chainId}`
}

/**
 * Build the namespaces we'll approve. Mirrors
 * `safe-wallet-monorepo/apps/web/src/features/walletconnect/services/WalletConnectWallet.ts`'s
 * `getNamespaces` byte-for-byte: only required chains (NOT optional),
 * required methods + SAFE_COMPATIBLE_METHODS, required events +
 * SAFE_COMPATIBLE_EVENTS.
 */
function buildNamespaces(proposal: WalletKitTypes.SessionProposal['params']) {
  console.log('[safeward] full proposal:', JSON.stringify(proposal, null, 2))

  const requiredChains = proposal.requiredNamespaces[EIP155]?.chains ?? []
  const supportedChainIds = [String(CHAIN_ID)].concat(
    requiredChains.map(stripEip155Prefix),
  )
  const eip155Chains = Array.from(new Set(supportedChainIds)).map((id) =>
    getEip155ChainId(id),
  )
  const accounts = eip155Chains.map((c) => `${c}:${MAIN_SAFE}`)

  // Required methods and events get merged with our compatible set, exactly
  // like Safe{Wallet}. Optional namespace methods/events are intentionally
  // dropped — Safe{Wallet}'s comment: "Don't include optionalNamespaces
  // methods/events".
  const requiredMethods = proposal.requiredNamespaces[EIP155]?.methods ?? []
  const requiredEvents = proposal.requiredNamespaces[EIP155]?.events ?? []
  const methods = Array.from(
    new Set([...requiredMethods, ...SUPPORTED_METHODS]),
  )
  const events = Array.from(new Set([...requiredEvents, ...SUPPORTED_EVENTS]))

  const approved = buildApprovedNamespaces({
    proposal,
    supportedNamespaces: {
      [EIP155]: { chains: eip155Chains, methods, events, accounts },
    },
  })
  console.log(
    '[safeward] approved namespaces:',
    JSON.stringify(approved, null, 2),
  )
  return approved
}

export async function approveSession(proposalId: number) {
  const wk = await getWalletKit()
  const proposal = Object.values(wk.getPendingSessionProposals()).find(
    (p) => p.id === proposalId,
  ) as WalletKitTypes.SessionProposal['params'] | undefined
  if (!proposal) throw new Error(`No pending proposal ${proposalId}`)
  const namespaces = buildNamespaces(proposal)

  const chainHexes = (namespaces[EIP155]?.chains ?? []).map(
    (c) => `0x${Number(stripEip155Prefix(c)).toString(16)}`,
  )
  const sessionProperties = buildSessionProperties(chainHexes)
  console.log('[safeward] sessionProperties:', sessionProperties)

  return wk.approveSession({ id: proposalId, namespaces, sessionProperties })
  // Note: dropped the post-approve chainChanged emit. Safe{Wallet} does it
  // but suspect it's racing the keychain commit and contributing to the
  // "No matching key" errors we see on first messages.
}

export async function rejectSession(proposalId: number) {
  const wk = await getWalletKit()
  return wk.rejectSession({
    id: proposalId,
    reason: getSdkError('USER_REJECTED'),
  })
}

export async function pair(uri: string) {
  const wk = await getWalletKit()
  return wk.pair({ uri })
}

export async function disconnectSession(topic: string) {
  const wk = await getWalletKit()
  return wk.disconnectSession({
    topic,
    reason: getSdkError('USER_DISCONNECTED'),
  })
}

/**
 * Nuke every persisted WalletKit session + pairing in IndexedDB. Use this
 * when "No matching key" / orphaned-session errors surface — they happen
 * if the dApp side disconnected without notifying us, or if WalletKit's
 * state otherwise got out of sync with reality. Pairings are lightweight
 * and can be safely re-established.
 */
export async function resetAllSessions(): Promise<void> {
  const wk = await getWalletKit()
  const active = Object.values(wk.getActiveSessions())
  for (const s of active) {
    try {
      await wk.disconnectSession({
        topic: s.topic,
        reason: getSdkError('USER_DISCONNECTED'),
      })
    } catch (err) {
      console.warn('[safeward] disconnect failed for', s.topic, err)
    }
  }
  const proposals = Object.values(wk.getPendingSessionProposals())
  for (const p of proposals) {
    try {
      await wk.rejectSession({ id: p.id, reason: getSdkError('USER_REJECTED') })
    } catch (err) {
      console.warn('[safeward] reject failed for proposal', p.id, err)
    }
  }
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('wc@2') || key.startsWith('safeward')) {
      localStorage.removeItem(key)
    }
  }
}
